// frontend/src/components/reporting/activity/UserConversationListPage.js — RPT-002
// User Conversation List. Two entry paths render the same component:
//   - drill-down (SuperAdmin/Admin/Coach): /app/reports/activity/users/:userId
//   - self entry (all 4 roles):            /app/my-activity
// See documentation/reporting-dashboard/report-catalog-filled-instances.md.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useRoles } from '../../../hooks/useRoles';
import { useApiCall } from '../../../hooks/useApiCall';
import apiService from '../../../services/apiService';
import { exportToCSV, formatDateForCSV } from '../../../utils/csvExport';
import '../../../styles/activityReporting.css';

const titleCase = (s) => {
  if (!s) return '—';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Anything other than the two known methodologies renders as blank, not an error.
const METHODOLOGY_LABELS = { basic: 'Case Study', real: 'Roleplay' };
const formatMethodologyLabel = (m) => METHODOLOGY_LABELS[m] || '';

const UserConversationListPage = () => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const navigate = useNavigate();
  const { userId: routeUserId } = useParams();
  const [searchParams] = useSearchParams();

  const isSelfEntry = !routeUserId;
  const [header, setHeader] = useState(null);
  const [conversations, setConversations] = useState([]);
  // RPT-003 link visibility comes from the backend (canViewDebrief), which
  // applies RPT-003's own access decision — e.g. an Admin who also coaches
  // this user sees it, a plain Admin doesn't. Hiding it is UX only; RPT-003
  // re-checks access server-side.
  const [canDrillToDebrief, setCanDrillToDebrief] = useState(false);
  const { loading, error, makeRequest } = useApiCall();

  const targetTenantId = searchParams.get('targetTenantId') || userProfile?.tenant?.id || null;

  const loadConversations = useCallback(async () => {
    if (!targetTenantId) return;
    try {
      const endpoint = isSelfEntry
        ? `/api/v1/reporting/user-activity/me/conversations?targetTenantId=${targetTenantId}`
        : `/api/v1/reporting/user-activity/${routeUserId}/conversations?targetTenantId=${targetTenantId}`;

      const data = await makeRequest(
        () => apiService.get(endpoint, user),
        'Loading conversations…'
      );
      if (data.success) {
        setHeader(data.data.header);
        setConversations(data.data.conversations || []);
        setCanDrillToDebrief(!!data.data.canViewDebrief);
      }
    } catch (err) {
      console.error('Failed to load user conversation list:', err);
    }
  }, [isSelfEntry, routeUserId, targetTenantId, user, makeRequest]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const handleRowClick = (conversationId, conversationTitle) => {
    const state = { conversationTitle };
    if (isSelfEntry) {
      navigate(`/app/my-activity/conversations/${conversationId}`, { state });
    } else {
      navigate(`/app/reports/activity/users/${routeUserId}/conversations/${conversationId}?targetTenantId=${targetTenantId}`, { state });
    }
  };

  const handleDownloadCSV = () => {
    if (!conversations.length) return;
    const columns = [
      { key: 'conversationTitle', label: 'Conversation' },
      { key: 'conversationType', label: 'Type' },
      { key: 'methodology', label: 'Methodology', format: formatMethodologyLabel },
      { key: 'lastActivityAt', label: 'Last Activity', format: formatDateForCSV },
      { key: 'currentPhase', label: 'Phase' },
      { key: 'currentSubphase', label: 'Subphase' },
      { key: 'messageCount', label: 'Messages' },
    ];
    const filename = `conversations-${(header?.displayName || header?.username || 'user').replace(/[^a-z0-9-]/gi, '-').toLowerCase()}`;
    const result = exportToCSV(conversations, columns, filename);
    if (!result.success) alert(`Failed to export CSV: ${result.error}`);
  };

  return (
    <div className="activity-report-page">
      <div className="arp-breadcrumb">
        {isSelfEntry ? (
          <span>My Activity</span>
        ) : (
          <>
            <Link to="/app/reports" className="arp-breadcrumb-link">Reporting Dashboard</Link>
            <CaretRight size={11} weight="bold" />
            <Link to={`/app/reports/activity?targetTenantId=${targetTenantId}`} className="arp-breadcrumb-link">User Activity</Link>
            <CaretRight size={11} weight="bold" />
            <span>{header?.displayName || header?.username || 'Conversations'}</span>
          </>
        )}
      </div>

      <div className="arp-header">
        <div>
          <h1 className="arp-title">{isSelfEntry ? 'My Conversations' : (header?.displayName || header?.username || 'Conversations')}</h1>
          <p className="arp-subtitle">{isSelfEntry ? 'Your conversations and their current status.' : header?.email}</p>
        </div>
        <div className="arp-actions">
          <button className="arp-btn arp-btn-secondary" onClick={handleDownloadCSV} disabled={!conversations.length}>
            Export CSV
          </button>
        </div>
      </div>

      {header && (
        <div className="arp-card arp-summary-card">
          <div className="arp-summary-stat">
            <span className="arp-summary-stat-label">Last Activity</span>
            <span className="arp-summary-stat-value">
              {header.lastActivityAt ? new Date(header.lastActivityAt).toLocaleString() : 'Never'}
            </span>
          </div>
          <div className="arp-summary-stat">
            <span className="arp-summary-stat-label">Conversations</span>
            <span className="arp-summary-stat-value">{header.conversationCount}</span>
          </div>
          <div className="arp-summary-stat">
            <span className="arp-summary-stat-label">Messages</span>
            <span className="arp-summary-stat-value">{header.totalMessageCount}</span>
          </div>
        </div>
      )}

      {error && <div className="arp-error-state">{error}</div>}

      {loading ? (
        <div className="arp-state">Loading…</div>
      ) : conversations.length === 0 ? (
        <div className="arp-state">No conversations found.</div>
      ) : (
        <div className="arp-table-wrapper">
          <table className="arp-table">
            <thead>
              <tr>
                <th>Conversation</th>
                <th>Type</th>
                <th>Last Activity</th>
                <th>Phase</th>
                <th>Messages</th>
                {canDrillToDebrief && <th>Debrief</th>}
              </tr>
            </thead>
            <tbody>
              {conversations.map((c) => (
                <tr key={c.conversationId}>
                  <td>
                    {canDrillToDebrief ? (
                      <button className="arp-row-link" onClick={() => handleRowClick(c.conversationId, c.conversationTitle)}>
                        {c.conversationTitle}
                      </button>
                    ) : (
                      c.conversationTitle
                    )}
                  </td>
                  <td className="arp-cell-muted">
                    {titleCase(c.conversationType)}{formatMethodologyLabel(c.methodology) ? ` · ${formatMethodologyLabel(c.methodology)}` : ''}
                  </td>
                  <td className="arp-cell-muted">
                    {c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleString() : 'Never'}
                  </td>
                  <td className="arp-cell-muted">
                    {titleCase(c.currentPhase)}{c.currentSubphase ? ` · ${titleCase(c.currentSubphase)}` : ''}
                  </td>
                  <td>{c.messageCount}</td>
                  {canDrillToDebrief && (
                    <td>
                      <button className="arp-row-link" onClick={() => handleRowClick(c.conversationId, c.conversationTitle)}>
                        View debrief
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserConversationListPage;
